import renderComponent, { FieldTypeWithOnlyValues } from '@databases/field-value';
import { ViewComponentProps } from '@databases/fields/view/types';
import { defaultStoreUtils } from '@databases/redux';
import {
  ConnectionContentWithColumns,
  getReferencedContentById,
  getReferencedContentStatusById,
} from '@databases/redux/connections/selectors';
import { SkeletonItem } from '@databases/skeleton';
import { DatabaseFieldType, DisplayedDetailType } from '@databases/types/database';
import { MIN_CARD_WIDTH } from '@databases/types/database/view';
import { TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH } from '@databases/utils/constants';
import useView from '@databases/views/useView';
import React, { FunctionComponent, useCallback, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

const Container = styled.ul`
  list-style-type: none;
  padding: 0;
  white-space: pre-line;

  > li + li {
    margin-top: 6px;
  }

  img {
    // Images in excerpt
    max-width: 100%;
  }
`;

const ContentDetailsValueView: FunctionComponent<
  ViewComponentProps<
    | DatabaseFieldType.PAGE_DETAILS
    | DatabaseFieldType.PAGE_LABELS
    | DatabaseFieldType.PAGE_STATUS
  >
> = ({ contentId, field, entryId }) => {
  const { view } = useView();

  const getContentField = useCallback(() => {
    let contentField: string | undefined;
    switch (field.type) {
      case DatabaseFieldType.PAGE_DETAILS:
        contentField = field.settings.contentField;
        break;
      case DatabaseFieldType.PAGE_LABELS:
        contentField = 'labels';
        break;
      case DatabaseFieldType.PAGE_STATUS:
        contentField = 'pageStatus';
        break;
    }

    if (!contentField) {
      return null;
    }

    if (contentField === 'versionNumber' && field.settings.includeVersionComment) {
      contentField = 'versionComment';
    }

    return contentField;
  }, [field]);

  const referencedContent = defaultStoreUtils.useEqualSelector((state) => {
    return getReferencedContentById(contentId, field, entryId ?? '', state) ?? [];
  });

  const referencedContentStatus = defaultStoreUtils.useEqualSelector((state) => {
    return (
      getReferencedContentStatusById(
        contentId,
        field,
        entryId ?? '',
        getContentField() || '',
        state
      ) ?? []
    );
  });

  // merge the content and status arrays
  const referencedContentWithStatus = referencedContentStatus.map((status, i) => ({
    content: referencedContent[i],
    status: status,
  }));

  const renderDetail = useCallback(
    (content: ConnectionContentWithColumns) => {
      const contentField = getContentField();
      if (!contentField) {
        return <>&nbsp;</>;
      }

      if (content.data[contentField]) {
        const value = content.data[contentField];
        return renderComponent({
          type: value.type,
          values: Array.isArray(value.data) ? value.data : [value.data],
          text: value.text,
        } as FieldTypeWithOnlyValues);
      }

      return <>&nbsp;</>;
    },
    [getContentField]
  );

  const containerRef = useRef<HTMLUListElement>(null);

  useLayoutEffect(() => {
    // The excerpt may contain links, which Confluence will render without a target attribute.
    // In order to avoid them being opened inside the iframe, we need to add a target.
    if (
      field.settings.displayedDetail === DisplayedDetailType.EXCERPT &&
      containerRef.current
    ) {
      // @ts-ignore
      for (const el of containerRef.current.querySelectorAll('a:not([target])')) {
        el.setAttribute('target', '_blank');
      }
    }
  });

  const layoutType = view.settings.layout.type;
  const skeletonWidth =
    layoutType === 'table'
      ? (field?.width ? field.width : TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH) * 0.7
      : MIN_CARD_WIDTH[view.settings.layout.cardWidth] * 0.6;

  if (referencedContentWithStatus.length === 0) {
    return null;
  } else {
    return (
      <Container ref={containerRef}>
        {referencedContentWithStatus.map(({ content, status }, i) => {
          const skeletonEnabled = !status?.status || status?.status === 'pending';
          return (
            <li key={i}>
              {content ? (
                renderDetail(content)
              ) : skeletonEnabled ? (
                <SkeletonItem minWidth={skeletonWidth} isEnabled>
                  <>&nbsp;</>
                </SkeletonItem>
              ) : (
                <>&nbsp;</>
              )}
            </li>
          );
        })}
      </Container>
    );
  }
};

export default ContentDetailsValueView;
